import React from 'react';
import PropTypes from 'prop-types';
import { Box, Spacing } from '@reservamos/elements';
import BreakdownPricing from 'ui/atoms/BreakdownPricing';
import { useTranslation } from 'react-i18next';
import useWhitelabelFeatures from 'hooks/whitelabel/useWhitelabelFeatures';
import adultIcon from '../../images/brands/gfa/avatars/adult.png';
import minorIcon from '../../images/brands/gfa/avatars/minor.png';
import studentIcon from '../../images/brands/gfa/avatars/student.png';
import teacherIcon from '../../images/brands/gfa/avatars/teacher.png';
import insenIcon from '../../images/brands/gfa/avatars/old-adult.png';
import adultIconVariant from '../../images/brands/etn/avatars/adult.png';
import minorIconVariant from '../../images/brands/etn/avatars/minor.png';
import studentIconVariant from '../../images/brands/etn/avatars/student.png';
import teacherIconVariant from '../../images/brands/etn/avatars/teacher.png';
import insenIconVariant from '../../images/brands/etn/avatars/old-adult.png';
import adultIconBrand from '../../images/brands/empresaunida/icons/ic-adult-seat.png';
import olderIconBrand from '../../images/brands/empresaunida/icons/ic-older-seat.png';
import disadvantagedYouthIconBrand from '../../images/brands/empresaunida/icons/ic-disadvantaged-youth-seat.png';
import disabilityIconBrand from '../../images/brands/empresaunida/icons/ic-disability-seat.png';

const types = {
  minor: {
    icon: minorIcon,
    iconVariant: minorIconVariant,
    label: 'available_minor_seats',
  },
  older: {
    icon: insenIcon,
    iconVariant: insenIconVariant,
    iconBrand: olderIconBrand,
    label: 'available_older_seats',
  },
  older_half: {
    icon: insenIcon,
    iconVariant: insenIconVariant,
    iconBrand: olderIconBrand,
    label: 'available_older_half_seats',
  },
  general: {
    icon: adultIcon,
    iconVariant: adultIconVariant,
    iconBrand: adultIconBrand,
    label: 'available_adult_seats',
  },
  oversold: {
    icon: adultIcon,
    iconVariant: adultIconVariant,
    label: 'available_adult_seats',
  },
  student: {
    icon: studentIcon,
    iconVariant: studentIconVariant,
    label: 'available_student_seats',
  },
  teacher: {
    icon: teacherIcon,
    iconVariant: teacherIconVariant,
    label: 'available_teacher_seats',
  },
  free_pass: {
    icon: adultIcon,
    iconVariant: adultIconVariant,
    iconBrand: disabilityIconBrand,
    label: 'available_free_pass_seats',
  },
  disadvantaged_youth: {
    icon: minorIcon,
    iconVariant: minorIconVariant,
    iconBrand: disadvantagedYouthIconBrand,
    label: 'available_disadvantaged_youth_seats',
  },
  disadvantaged_youth_half: {
    icon: minorIcon,
    iconVariant: minorIconVariant,
    iconBrand: disadvantagedYouthIconBrand,
    label: 'available_disadvantaged_youth_half_seats',
  },
  disability: {
    icon: null,
    iconVariant: null,
    iconBrand: disabilityIconBrand,
    label: 'available_disability_seats',
  },
  disability_half: {
    icon: null,
    iconVariant: null,
    iconBrand: disabilityIconBrand,
    label: 'available_disability_half_seats',
  },
  federal_companion_free_pass: {
    icon: adultIcon,
    iconVariant: adultIconVariant,
    label: 'available_federal_companion_free_pass_seats',
  },
  wheelchair_handicap: {
    icon: null,
    iconVariant: null,
    label: 'available_wheelchair_handicap_seats',
  },
  special: {
    icon: null,
    iconVariant: null,
    label: 'available_special_seats',
  },
  pet_friendly: {
    icon: null,
    iconVariant: null,
    label: 'available_pet_friendly_seats',
  },
};

const FALLBACK_NORMAL_DISCOUNT = ' ';

/**
 * Renders the CategoriesPricing component, which displays pricing breakdown
 * for different passenger categories, including discounts and icons.
 *
 * @param {Object} props - The component props.
 * @param {string} props.type - The type of passenger (e.g., "minor", "general").
 * @param {boolean} props.hasDiscountAnticipated - Whether there is an anticipated discount.
 * @param {Array|number} props.subDiscounts - Sub-discount details or count.
 * @param {number} props.discount - The discount percentage.
 * @param {boolean} props.isFlat - Whether to use variant icons.
 * @param {number} props.total - The total price.
 * @param {number} props.countWithSpecialDiscount - Number of passengers with special discounts.
 * @param {number} props.totalSpecialDiscount - Total amount for special discounts.
 * @param {number} props.countWithoutSpecialDiscount - Number of passengers without special discounts.
 * @param {number} props.normalDiscountPercent - Discount percentage for non-special cases.
 * @param {number} props.normalTotal - Total price for non-special cases.
 * @param {Array} props.pricingSegments - Fare segments to render when mixed prices apply.
 * @returns {JSX.Element} The rendered CategoriesPricing component.
 */
const CategoriesPricing = ({
  type,
  subDiscounts,
  discount,
  hasDiscountAnticipated,
  isFlat,
  total,
  countWithSpecialDiscount,
  totalSpecialDiscount,
  countWithoutSpecialDiscount,
  normalDiscountPercent,
  normalTotal,
  pricingSegments,
}) => {
  const { DISCOUNT_BADGE_RESULT_ENABLED, FREE_FARE_BRAND_GLYPHS } = useWhitelabelFeatures();
  const useBrandIcons = FREE_FARE_BRAND_GLYPHS;
  const { icon, iconVariant, iconBrand, label } = types?.[type] || {};
  const { t } = useTranslation('search');
  const hasPricingSegments = pricingSegments?.length > 1;

  const resolvedIcon = (useBrandIcons && iconBrand) || (isFlat ? iconVariant : icon);

  return (
    <Box
      paddingHorizontal="XS"
      paddingVertical="XS"
      borderRadius="M"
      alphaBg="XS"
      bgColor="primary"
    >
      <Spacing size="XXS" vertical>
        <BreakdownPricing
          label={t(label, { count: subDiscounts })}
          price={total}
          discountIcon={hasPricingSegments ? '' : hasDiscountAnticipated}
          typeIcon={resolvedIcon}
          typeIconSize={useBrandIcons ? 'L' : 'S'}
          discount={
            hasPricingSegments || (countWithSpecialDiscount && countWithoutSpecialDiscount)
              ? ''
              : discount
          }
          showDiscountBadge={DISCOUNT_BADGE_RESULT_ENABLED}
        />
        {hasPricingSegments &&
          pricingSegments.map((segment) => (
            <Spacing size="XS" vertical key={segment.id}>
              <BreakdownPricing
                label={t(label, { count: segment.count })}
                price={segment.total}
                subDiscount={segment.discountPercent || FALLBACK_NORMAL_DISCOUNT}
                discountIcon={segment.hasDiscountIcon ? hasDiscountAnticipated : ''}
                showDiscountBadge={DISCOUNT_BADGE_RESULT_ENABLED}
              />
            </Spacing>
          ))}
        {!hasPricingSegments && !!normalTotal && !!countWithSpecialDiscount && (
          <Spacing size="XS" vertical>
            <BreakdownPricing
              label={t(label, { count: countWithSpecialDiscount })}
              price={totalSpecialDiscount}
              subDiscount={discount}
              discountIcon={hasDiscountAnticipated}
              showDiscountBadge={DISCOUNT_BADGE_RESULT_ENABLED}
            />
          </Spacing>
        )}
        {!hasPricingSegments && !!normalTotal && !!countWithSpecialDiscount && (
          <Spacing size="XS" vertical>
            <BreakdownPricing
              label={t(label, { count: countWithoutSpecialDiscount })}
              price={normalTotal}
              subDiscount={normalDiscountPercent || FALLBACK_NORMAL_DISCOUNT}
              showDiscountBadge={DISCOUNT_BADGE_RESULT_ENABLED}
            />
          </Spacing>
        )}
      </Spacing>
    </Box>
  );
};

CategoriesPricing.propTypes = {
  type: PropTypes.oneOf(Object.keys(types)).isRequired,
  discount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  subDiscounts: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  hasDiscountAnticipated: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  isFlat: PropTypes.bool,
  total: PropTypes.number,
  countWithSpecialDiscount: PropTypes.number,
  totalSpecialDiscount: PropTypes.number,
  countWithoutSpecialDiscount: PropTypes.number,
  normalDiscountPercent: PropTypes.string,
  normalTotal: PropTypes.number,
  pricingSegments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      count: PropTypes.number,
      total: PropTypes.number,
      discountPercent: PropTypes.string,
      hasDiscountIcon: PropTypes.bool,
    }),
  ),
};

CategoriesPricing.defaultProps = {
  discount: 0,
  isFlat: false,
};

export default CategoriesPricing;
